import { createTheme, ThemeProvider } from '@mui/material/styles';
import { createContext, useContext, useState, useEffect } from 'react';
import { IContextChildren } from '../interfaces/context-children.inteface';

// Theme context interface
interface IThemeContext {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

// Create context
export const ThemeToggleContext = createContext<IThemeContext>({
  isDarkMode: false,
  toggleDarkMode: () => {},
});

// Custom hook to use theme toggle
export const useThemeToggle = () => {
  const context = useContext(ThemeToggleContext);
  if (!context) {
    throw new Error('useThemeToggle must be used within a CustomThemeProvider');
  }
  return context;
};

// You can also create different themes for light/dark mode
const createCustomTheme = (isDarkMode: boolean) => createTheme({
  palette: {
    mode: isDarkMode ? 'dark' : 'light',
    primary: {
      main: isDarkMode ? '#111' : '#587b94',
      light: '#42a5f5',
      dark: '#005b7f',
      contrastText: '#fff',
    },
    secondary: {
      main: '#304758',
      light: isDarkMode ? '#f48fb1' : '#ff5983',
      dark: '#005b7f',
      contrastText: '#fff',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: isDarkMode ? '#121212' : '#fafafa',
      paper: isDarkMode ? '#1e1e1e' : '#fff',
    },
    text: {
      primary: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
      secondary: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
    },
  },
  typography: {
    fontFamily: [
      '"Roboto"',
      '"Helvetica"',
      '"Arial"',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    button: {
      textTransform: 'uppercase',
      fontWeight: 500,
      borderRadius: '1.5em'
    },
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '1.5em',
          padding: '0.75em 1.5em',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '3px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: isDarkMode ? '0 2px 8px rgba(255,255,255,0.1)' : '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: '6px',
        },
      },
    },
  },
});

export const CustomThemeProvider = ({ children }: IContextChildren) => {
  // Get initial dark mode state from localStorage or default to false
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Save dark mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = createCustomTheme(isDarkMode);

  return (
    <ThemeToggleContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeToggleContext.Provider>
  );
};
